import { reactive } from 'vue'
import {
  normalizeMultiOrgIds,
  normalizeSelectedKeys,
  readOrganizationState,
  writeOrganizationState,
} from '../storage/organizationStorage'

const listeners = new Set()
const organizationState = reactive(readOrganizationState())

function replaceState(nextState) {
  Object.keys(organizationState).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(nextState, key)) {
      delete organizationState[key]
    }
  })

  Object.assign(organizationState, nextState)
  listeners.forEach((listener) => listener({...organizationState}))
}

export function getOrganizationState() {
  return organizationState
}

export function getOrganizationSnapshot() {
  return {...organizationState}
}

export function hydrateOrganizationContext(storage = localStorage) {
  replaceState(readOrganizationState(storage))
  return getOrganizationSnapshot()
}

export function subscribeOrganizationContext(listener) {
  listeners.add(listener)
  listener(getOrganizationSnapshot())

  return () => {
    listeners.delete(listener)
  }
}

export function setWithChildOrgs(withChildOrgs, storage = localStorage) {
  replaceState(writeOrganizationState({withChildOrgs}, storage))
  return getOrganizationSnapshot()
}

export function setSingleOrganization(org, breadcrumbs = [], storage = localStorage) {
  const orgId = org?.id ?? org?.key ?? org
  replaceState(writeOrganizationState({
    mode: 'single',
    orgId,
    orgData: breadcrumbs,
    multiOrgIds: [],
    multiOrgSelectedKeys: {},
    multiOrgData: [],
  }, storage))

  return getOrganizationSnapshot()
}

export function setMultiOrganizations({selectedKeys = {}, orgIds = null, breadcrumbs = [], withChildOrgs = null} = {}, storage = localStorage) {
  const normalizedSelectedKeys = normalizeSelectedKeys(selectedKeys)
  const normalizedOrgIds = orgIds === null
    ? Object.keys(normalizedSelectedKeys).map((id) => ({id}))
    : normalizeMultiOrgIds(orgIds)

  replaceState(writeOrganizationState({
    mode: 'multi',
    multiOrgIds: normalizedOrgIds,
    multiOrgSelectedKeys: normalizedSelectedKeys,
    multiOrgData: breadcrumbs,
    withChildOrgs: withChildOrgs === null ? organizationState.withChildOrgs : withChildOrgs,
  }, storage))

  return getOrganizationSnapshot()
}
